/* eslint-disable react/jsx-wrap-multilines */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Constants } from '@pwc/pwc-deals-web-common';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { linkedAdjustmentsShape } from '../../../../../common/components/proptypes/commonProptypes';
import { isLastNPeriodLabel } from '../../../../../common/services/textService';
import AdjustmentToggle from '../AdjustmentToggle';
import FinancialValue from '../FinancialValue';
import {
  getAnalysisDescription,
  setDescriptorForAdjustmentThunk,
  getIsAdjustmentLoading,
} from '../../../../../common/engine/reducers/structuredDocumentDescriptionSlice';
import AdjustmentControlsColumn from '../AdjustmentControlsColumn';
import AdjustmentLinkOverlayTrigger from '../LinkedAdjustmentPopover/LinkedAdjustmentsOverlayTrigger';
import LinkedAdjustmentEditOverlayTrigger from '../LinkedAdjustmentPopover/LinkedAdjustmentsEditOverlayTrigger';

const Adjustment = ({
  projectId,
  documentCode = Constants.QUALITY_OF_EARNINGS,
  contentId,
  contentVersionNumber,
  adjustmentType,
  adjustmentKey,
  adjustment,
  timePeriods,
  adjustmentLinks,
  isActive,
  showToggle,
  disableToggle,
  isPublishMode,
  isFinalRow,
  isEditMode,
  adjustmentOptions,
  targetDocumentOptions,
  selectedLinkedAdjustments,
  setFieldValue,
  showLinkedAdjustments,
  isClientView = false,
}) => {
  const dispatch = useDispatch();
  const isNwcTable = documentCode === Constants.NET_WORKING_CAPITAL;
  const analysisDescription = useSelector(getAnalysisDescription);
  const sourceAdjustment = `${documentCode}_${adjustmentType}_${adjustmentKey.replace(
    ' ',
    '_'
  )}`;
  const [showOverlay, setShowOverlay] = useState(false);
  const onToggle = (isShowing) => {
    setShowOverlay(isShowing);
  };
  const isAdjustmentLoading = useSelector(getIsAdjustmentLoading);

  useEffect(() => {
    // hack to hide the linked adjustment overlay otherwise it will
    // show on top of other components when scrolling
    const doc = document.querySelector('div.scrollable.structured-table');
    if (doc) {
      doc.onscroll = () => {
        if (!showOverlay) {
          document.body.click();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setShowOverlay(true);
    }
    if (e.key === 'Tab') {
      setShowOverlay(false);
    }
  };

  return (
    <tr
      role="row"
      className={classnames('adjustment', {
        inactive: !isActive,
        'final-details-row': isFinalRow,
      })}
      data-testid="adjustment"
    >
      <th
        role="rowheader"
        scope="row"
        className={`adjustment__heading ${
          analysisDescription.selectedAdjustment === adjustmentKey
            ? 'adjustment-selected'
            : ''
        }`}
      >
        {isPublishMode ? (
          <span>{adjustmentKey}</span>
        ) : (
          <button
            className="button-avoid-focus adjustment__button"
            onClick={() => {
              dispatch(
                setDescriptorForAdjustmentThunk(
                  adjustmentType,
                  adjustmentKey,
                  documentCode,
                  projectId,
                  contentId,
                  contentVersionNumber,
                  isClientView
                )
              );
            }}
            type="button"
            data-testid="adjustment-header-button"
            aria-controls="analysisDescription"
            disabled={isAdjustmentLoading}
          >
            <span className="adjustment-title">{adjustmentKey}</span>
            <span
              data-testid="adjustment-button-helper-text"
              className="visually-hidden"
            >
              . Click for long description
            </span>
          </button>
        )}
      </th>
      {timePeriods.map((timePeriodInput) => {
        const timePeriod = timePeriodInput.toString();
        const isLastNPeriod = isLastNPeriodLabel(timePeriod);
        const showColumnHighlight = isNwcTable && isLastNPeriod;

        return (
          <FinancialValue
            key={timePeriod}
            text={adjustment[timePeriod]}
            showColumnHighlight={showColumnHighlight}
          />
        );
      })}
      {!isPublishMode && (
        <AdjustmentControlsColumn>
          <div>
            {showToggle && (
              <AdjustmentToggle
                isActive={isActive}
                disabled={disableToggle}
                documentCode={documentCode}
                adjustmentType={adjustmentType}
                adjustmentKey={adjustmentKey}
              />
            )}
          </div>
          {!isEditMode && (
            <AdjustmentLinkOverlayTrigger
              adjustmentLinks={adjustmentLinks}
              projectId={projectId}
              handleKeyDown={handleKeyDown}
              show={showOverlay}
              onToggle={onToggle}
            />
          )}
          {isEditMode && showLinkedAdjustments && (
            <LinkedAdjustmentEditOverlayTrigger
              adjustmentLinks={adjustmentLinks}
              projectId={projectId}
              adjustmentOptions={adjustmentOptions}
              targetDocumentOptions={targetDocumentOptions}
              selectedLinkedAdjustments={selectedLinkedAdjustments}
              setFieldValue={setFieldValue}
              sourceAdjustment={sourceAdjustment}
              show={showOverlay}
              onToggle={onToggle}
              handleKeyDown={handleKeyDown}
            />
          )}
        </AdjustmentControlsColumn>
      )}
    </tr>
  );
};

Adjustment.propTypes = {
  projectId: PropTypes.string.isRequired,
  documentCode: PropTypes.string.isRequired,
  contentId: PropTypes.number.isRequired,
  contentVersionNumber: PropTypes.number.isRequired,
  adjustmentType: PropTypes.string.isRequired,
  adjustmentKey: PropTypes.string.isRequired,
  adjustment: PropTypes.shape({}).isRequired,
  timePeriods: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  adjustmentLinks: PropTypes.arrayOf(linkedAdjustmentsShape),
  isActive: PropTypes.bool,
  showToggle: PropTypes.bool,
  disableToggle: PropTypes.bool,
  isPublishMode: PropTypes.bool.isRequired,
  isFinalRow: PropTypes.bool,
};

Adjustment.defaultProps = {
  adjustmentLinks: [],
  isActive: true,
  showToggle: true,
  disableToggle: false,
  isFinalRow: false,
};

export default Adjustment;
